import type { PrimitiveNode } from "../layout/types.js";
import { applyPatchToImageNode } from "./applyRuntimePatches.js";
import { collectBehaviorDependencies } from "./compileEvents.js";
import { compileBehavior } from "./compileBehavior.js";
import { evaluateWithRenderCache } from "./renderCache.js";
import {
  applyRuntimeDomTargetFlags,
  runCompiledNodeBehavior,
  withCurrentNodeGeometry,
} from "./applyNodeBehavior.js";
import type {
  CompiledImageNode,
  CompileNodeContext,
  RenderImageNode,
  RuntimeContext,
} from "./types.js";
import {
  compileNodeStyle,
  resolvePrimitiveTooltip,
  transitionsTouchTopLeft,
} from "./helpers.js";
import { uniqueDependencies } from "./method.js";

export function compileImageNode(
  node: PrimitiveNode,
  compileContext: CompileNodeContext,
): CompiledImageNode {
  if (node.kind !== "primitive" || node.primitive !== "image") {
    throw new Error("Node is not an image primitive");
  }

  const baseStyle = compileNodeStyle(node.style, node.transitions);
  const behavior = node.behavior
    ? compileBehavior(
        node.behavior,
        node.id,
        compileContext.catalogDeps,
        undefined,
        compileContext.layoutNodeById,
      )
    : undefined;

  const dependencies = uniqueDependencies([
    ...(behavior ? collectBehaviorDependencies(behavior, compileContext) : []),
    ...(node.tooltip?.kind === "layout"
      ? ["layoutListRowItem", "rowIndex"]
      : []),
  ]);

  return {
    id: node.id,
    dependencies,

    evaluate(ctx: RuntimeContext): RenderImageNode | null {
      return evaluateWithRenderCache(ctx, node.id, dependencies, () => {
        let renderNode: RenderImageNode = {
          id: node.id,
          key: ctx.__nodeInstanceKeyPrefix
            ? `${ctx.__nodeInstanceKeyPrefix}${node.id}`
            : node.id,
          x: node.x,
          y: node.y,
          width: node.width,
          height: node.height,
          zIndex: node.zIndex,
          rotation: node.rotation ?? 0,
          kind: "image",
          src: node.src ?? "",
          alt: node.label,
          style: { ...baseStyle },
          tooltip: resolvePrimitiveTooltip(node.tooltip, ctx),
          hasTopLeftTransition: transitionsTouchTopLeft(node.transitions),
        };

        if (behavior) {
          const behaviorCtx = withCurrentNodeGeometry(ctx, {
            x: node.x,
            y: node.y,
            width: node.width,
            height: node.height,
            rotation: node.rotation ?? 0,
          });
          const { patches, effects, continuations, runtimeDomTarget } =
            runCompiledNodeBehavior(behavior, behaviorCtx, node.id);

          for (const patch of patches) {
            renderNode = applyPatchToImageNode(renderNode, patch);
          }

          ctx.__runtimeEffects?.push(...effects);
          ctx.__runtimeContinuations?.push(...continuations);
          renderNode = applyRuntimeDomTargetFlags(
            renderNode,
            runtimeDomTarget,
            behavior,
          );
        }

        if (renderNode.style.visibility === false) {
          return null;
        }

        return renderNode;
      });
    },
  };
}
